/**
 * Client-side models for the cohesive hearing record (#PHMS-HREC S1) and the proceeding-scoped
 * capture (#PHMS-HREC S2). These mirror the server DTOs in
 * `Riley.Jms.Application.Models.HearingRecordDtos` and `ProceedingHearingCaptureDtos`. Only the
 * fields the pre-hearing capture surface (#PHMS-HREC S3) needs are typed here; the rest of the
 * record is intentionally left loose so the surface does not couple to blocks it does not render.
 */

/** Provenance marker on an assembled record block: which reader supplied it and whether it had data. */
export interface HearingRecordProvenance {
  source: string;
  hasData: boolean;
}

/** Slim person demographics from the context block (#PHMS-HREC S1). */
export interface IpDemographicsLike {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  cdcNumber?: string | null;
  custodyStatus?: string | null;
  custodyLevel?: string | null;
}

/** Demographics block from the S1 record (superset of {@link IpDemographicsLike}). */
export interface HearingRecordDemographics extends IpDemographicsLike {
  incarceratedPersonIdentifier?: string | null;
  dateOfBirth?: string | null;
  age?: number | null;
  gender?: string | null;
  raceEthnicity?: string | null;
  nationality?: string | null;
  languages?: string | null;
  facilityCode?: string | null;
  facilityLabel?: string | null;
  admitDate?: string | null;
  eligibilityDate?: string | null;
  projectedReleaseDate?: string | null;
  hasData?: boolean;
}

/** A single custody movement/term (#PHMS-HREC S1 context). */
export interface HearingRecordIncarcerationTerm {
  facilityLabel?: string | null;
  securityLevel?: string | null;
  startedOn?: string | null;
  endedOn?: string | null;
  isCurrent?: boolean;
}

/** Custody history block from the S1 record. */
export interface HearingRecordIncarcerationHistory {
  terms?: HearingRecordIncarcerationTerm[] | null;
  admitDate?: string | null;
  eligibilityDate?: string | null;
  projectedReleaseDate?: string | null;
}

/** A panel member on the scheduled proceeding. */
export interface HearingRecordPanelMember {
  id?: number | null;
  name?: string | null;
  roleCode?: string | null;
  isPresiding?: boolean;
}

/** A scheduled person/resource expected to participate in the hearing. */
export interface HearingRecordParticipant {
  id?: number | null;
  resourceTypeCode?: string | null;
  resourceKey?: string | null;
  name?: string | null;
  roleCode?: string | null;
}

/** A pre-hearing readiness requirement. */
export interface HearingRecordRequirement {
  label?: string | null;
  statusCode?: string | null;
}

/** The scheduled-proceeding block (next hearing context) from the S1 record. */
export interface HearingRecordProceeding {
  scheduledProceedingId?: number | null;
  title?: string | null;
  proceedingTypeCode?: string | null;
  scheduledStartUtc?: string | null;
  statusCode?: string | null;
  statusLabel?: string | null;
  format?: string | null;
  locationName?: string | null;
  roomName?: string | null;
  requirementsTotal?: number | null;
  requirementsComplete?: number | null;
  panel?: HearingRecordPanelMember[] | null;
  participants?: HearingRecordParticipant[] | null;
  requirements?: HearingRecordRequirement[] | null;
}

/** A commitment/controlling offense from the S1 record context. */
export interface HearingRecordOffense {
  recordCategoryCode?: string | null;
  statuteReference?: string | null;
  eventDate?: string | null;
  summary?: string | null;
  dispositionCode?: string | null;
}

/** Block 1 — context assembled from the existing person-scoped readers. */
export interface HearingRecordContextBlock {
  demographics?: HearingRecordDemographics | null;
  demographicsProvenance?: HearingRecordProvenance;
  incarcerationHistory?: HearingRecordIncarcerationHistory | null;
  priorHearings?: HearingRecordPriorHearing[] | null;
  proceeding?: HearingRecordProceeding | null;
  offenses?: HearingRecordOffense[] | null;
}

/** Read projection of the proceeding-scoped capture (#PHMS-HREC S2). */
export interface ProceedingHearingCaptureDto {
  id: number;
  scheduledProceedingId: number;
  sequence: number;
  eligibilityTrack?: string | null;
  citizenshipStatus?: string | null;
  statusCode: string;
  /** Raw stored narratives object as JSON text. */
  narrativesJson: string;
}

/** Block 2 — captured suitability content from S2. */
export interface HearingRecordCapturedBlock {
  capture?: ProceedingHearingCaptureDto | null;
  provenance?: HearingRecordProvenance;
}

/** The cohesive, proceeding-scoped hearing record (#PHMS-HREC S1). */
export interface HearingRecordDto {
  scheduledProceedingId: number;
  incarceratedPersonId?: number | null;
  subjectResolved: boolean;
  context: HearingRecordContextBlock;
  captured: HearingRecordCapturedBlock;
  decision?: HearingRecordDecisionBlock | null;
  derived?: HearingRecordDerivedFields | null;
}

/** Server-computed, read-only derived fields on the S1 record (#PHMS-HREC S1). Loose — the S6 surface
 * only consumes {@link governorReview} (PC 187 → gubernatorial review on a grant). */
export interface HearingRecordDerivedFields {
  isDerived?: boolean;
  ageAtOffense?: number | null;
  priorHearingCount?: number | null;
  governorReview?: boolean | null;
}

/**
 * Partial-upsert request for the S2 capture. Every member is optional: a member left null/undefined
 * is not touched on an existing record. `narratives` is shallow-merged into the stored narrative
 * object. Mirrors `ProceedingHearingCaptureUpsertRequest`.
 */
export interface ProceedingHearingCaptureUpsertRequest {
  sequence?: number;
  eligibilityTrack?: string | null;
  citizenshipStatus?: string | null;
  statusCode?: string | null;
  narratives?: Record<string, string | null>;
}

/**
 * Flattened form-values shape the pre-hearing surface binds. Typed (eligibility track, citizenship)
 * fields map to the capture's typed columns; the remaining narrative fields are merged into the
 * capture's `narratives` object on upsert.
 */
export interface PreHearingFormValues {
  eligibilityTrack: string;
  citizenshipStatus: string;
  riskCraNote: string;
  criminalHistoryConfirmed: boolean;
  criminalHistoryNote: string;
  institutionalBehaviorSummary: string;
  stakeholderInputSummary: string;
  releasePlanSummary: string;
  [key: string]: string | boolean;
}

/** Narrative keys the pre-hearing surface owns inside the capture's narratives object. */
export const PRE_HEARING_NARRATIVE_KEYS = [
  "riskCraNote",
  "criminalHistoryConfirmed",
  "criminalHistoryNote",
  "institutionalBehaviorSummary",
  "stakeholderInputSummary",
  "releasePlanSummary",
] as const;

export type PreHearingNarrativeKey = (typeof PRE_HEARING_NARRATIVE_KEYS)[number];

/* ------------------------------------------------------------------------- *
 * #PHMS-HREC S4 — at-hearing live-entry surface
 * ------------------------------------------------------------------------- */

/**
 * Statutory-style section the at-hearing tracker renders. Driven from a config array (not
 * hand-coded duplication): `id` keys the accordion + chip, `number` is the displayed statutory
 * number, and `requiredIncomplete` marks the chip with a ★ until its required fields are filled.
 */
export interface HearingSection {
  id: string;
  number: string;
  title: string;
  /** When true, the chip shows a ★ (required content still outstanding). */
  requiredIncomplete: boolean;
}

/** Acceptance-of-responsibility scale for §8 (radio). */
export const ACCEPTANCE_OPTIONS = [
  { value: "full", viewValue: "Full" },
  { value: "partial", viewValue: "Partial" },
  { value: "minimizes", viewValue: "Minimizes" },
  { value: "denies", viewValue: "Denies" },
] as const;

/** Panel finding for the §14 decision entry (radio). */
export const FINDING_OPTIONS = [
  { value: "suitable", viewValue: "Suitable" },
  { value: "unsuitable", viewValue: "Unsuitable" },
] as const;

/**
 * Narrative keys the at-hearing surface owns inside the capture's `narratives` object. These are
 * shallow-merged on upsert, so each field autosaves independently without clobbering the others
 * (including the pre-hearing keys, which live alongside these in the same capture).
 */
export const AT_HEARING_NARRATIVE_KEYS = [
  // §4 Commitment offense
  "offenseCircumstances",
  "offenseMotivation",
  "offenseUnderStress",
  "offenseNexus",
  // §8 Insight, remorse & accountability
  "insightCausativeFactors",
  "acceptanceOfResponsibility",
  "signsOfRemorse",
  "remorseVictimImpact",
  // Lighter sections
  "metadataNotes",
  "identityNotes",
  "criminalHistorySummary",
  "institutionalSummary",
  "riskSummary",
  "parolePlans",
  "stakeholderParticipation",
  // Live hearing session
  "hearingStartedUtc",
  "hearingEndedUtc",
  "attendeePresence",
] as const;

export type AtHearingNarrativeKey = (typeof AT_HEARING_NARRATIVE_KEYS)[number];

/** Flattened form model the at-hearing surface binds to ui-form. */
export interface AtHearingFormValues {
  offenseCircumstances: string;
  offenseMotivation: string;
  offenseUnderStress: boolean;
  offenseNexus: string;
  insightCausativeFactors: string;
  acceptanceOfResponsibility: string;
  signsOfRemorse: boolean;
  remorseVictimImpact: string;
  metadataNotes: string;
  identityNotes: string;
  criminalHistorySummary: string;
  institutionalSummary: string;
  riskSummary: string;
  parolePlans: string;
  stakeholderParticipation: string;
  hearingStartedUtc: string;
  hearingEndedUtc: string;
  attendeePresence: string;
  [key: string]: string | boolean;
}

/**
 * A single determining suitability factor on the §14 finalize request. Mirrors
 * `HearingDecisionFactorInput` on the server.
 */
export interface HearingDecisionFactorInput {
  factorCode?: string | null;
  findingCode?: string | null;
  findingText?: string | null;
  kind?: string | null;
}

/**
 * Body for `POST /api/v1/phms/hearing-records/{id}/decision` (the S5 finalize endpoint). Mirrors
 * `HearingDecisionFinalizeRequest`. `decisionRationale` is required server-side on both findings;
 * `denialLengthYears` applies only to an unsuitable finding.
 */
export interface HearingDecisionFinalizeRequest {
  finding?: string | null;
  decisionRationale?: string | null;
  votePosture?: string | null;
  decidedByUserId?: number | null;
  captureSequence?: number | null;
  denialLengthYears?: number | null;
  proposedReleaseDate?: string | null;
  specialConditions?: string[] | null;
  factors?: HearingDecisionFactorInput[] | null;
}

export interface DecisionArtifactResult {
  decisionRecordId?: number;
  outcome?: "created" | "disabled" | "decision_not_found" | "decision_not_finalized"
    | "frozen_wording_missing" | "adjudication_decision_not_found" | "legal_review_required"
    | "template_not_found" | "source_artifact_not_found" | string;
  mode?: "generated" | "registered" | string;
  artifactPublicId?: string | null;
  created?: boolean;
}

/** Result of the §14 finalize, including the automatic decision-document issuance attempt. */
export interface HearingDecisionFinalizeResult {
  scheduledProceedingId: number;
  outcome?: unknown;
  decision?: unknown;
  decisionArtifact?: DecisionArtifactResult | null;
}

/* ------------------------------------------------------------------------- *
 * #PHMS-HREC S6 — post-decision record + amend
 * ------------------------------------------------------------------------- */

/**
 * One determining suitability factor on the rendered outcome (#PHMS-HREC S5 → S6). Mirrors the server
 * `HearingDecisionFactorDto`. `factorCode` is one of `insight`, `cra_risk`, `institutional_conduct`,
 * `reentry_plans`, `commitment_nexus`; `kind` is `static` (immutable historical) or `dynamic`.
 */
export interface HearingDecisionFactor {
  factorCode?: string | null;
  findingCode?: string | null;
  findingText?: string | null;
  kind?: string | null;
}

/**
 * The rendered post-hearing decision outcome (#PHMS-HREC S5) the S6 record view binds. Mirrors the
 * server `HearingDecisionOutcomeDto` carried on `record.decision.outcome`. `finding` is `suitable`
 * (grant) or `unsuitable` (denial); the auto-derived `governorReview`/`adminReview` flags and the
 * `recordedByUserId`/`recordedUtc` provenance come straight from the finalized decision.
 */
export interface HearingDecisionOutcome {
  finding?: string | null;
  rationale?: string | null;
  denialLengthYears?: number | null;
  proposedReleaseDate?: string | null;
  specialConditions?: string[] | null;
  governorReview?: boolean | null;
  adminReview?: boolean | null;
  determiningFactors?: HearingDecisionFactor[] | null;
  recordedByUserId?: number | null;
  recordedUtc?: string | null;
}

/** The adjudication decision carried on `record.decision.decision`; read loosely — the S6 surface
 * only needs the recorded-by/at provenance which it prefers from {@link HearingDecisionOutcome}. */
export interface AdjudicationDecisionLike {
  id?: number | null;
  decisionCode?: string | null;
  recordedUtc?: string | null;
  [key: string]: unknown;
}

/** Block 3 — the latest adjudication decision for the proceeding (#PHMS-HREC S5/S6). */
export interface HearingRecordDecisionBlock {
  decision?: AdjudicationDecisionLike | null;
  outcome?: HearingDecisionOutcome | null;
  provenance?: HearingRecordProvenance;
}

/** A prior board hearing on the record context's hearing-history trail (#PHMS-HREC S1). */
export interface HearingRecordPriorHearing {
  id?: number | null;
  hearingDate?: string | null;
  hearingType?: string | null;
  decisionCode?: string | null;
  panelSummary?: string | null;
  notes?: string | null;
}
